/**
 * lp-express-pricing.ts
 * ─────────────────────
 * Single source of truth for the LP Express receipt model. The Papabear
 * template was originally hardcoded for a 4-piece BUNDLE ($121.90 retail →
 * $29, 76% off). `computeReceipt` derives the line items + totals from the
 * actual product(s), the built offer, and any attached sale so the receipts
 * render correctly for a single product, a multi-product bundle, with or
 * without a discount.
 */
import type { Product } from "@shared/schema";

export interface LpReceiptLine {
  name: string;
  /** Per-item price, already formatted (e.g. "$49.95"), or null when unknown. */
  priceLabel: string | null;
  desc?: string;
}

export interface LpReceipt {
  lineItems: LpReceiptLine[];
  isMulti: boolean;
  hasDiscount: boolean;
  /** Struck-through retail total — only set when there is a real discount. */
  retailLabel: string | null;
  /** e.g. "-$92.90" — only set when discounted. */
  discountAmountLabel: string | null;
  discountPercent: number | null;
  /** Row label for the discount line, e.g. the sale name or "Bundle savings". */
  discountRowLabel: string;
  priceTodayLabel: string;
  savingsLabel: string | null;
  perDayLabel: string | null;
  refillLabel: string | null;
  guaranteeDays: number;
  guaranteeBullets: string[];
  bundleLabel: string;
  /** Visible CTA wording, e.g. "GET YOURS FOR $49.95 TODAY". */
  ctaText: string;
}

interface OfferLike {
  priceToday?: string | null;
  retailTotal?: string | null;
  refillPrice?: string | null;
  guaranteeDays?: number | null;
}

interface SaleLike {
  name?: string | null;
  /** salesDiscounts.saleType — e.g. "percentage_off", "dollar_off". */
  saleType?: string | null;
  /** salesDiscounts.discountValue — e.g. "76" (%), "30" ($). */
  discountValue?: string | null;
}

export function parsePrice(raw?: string | null): number | null {
  if (raw == null) return null;
  const cleaned = String(raw).replace(/[^\d.]/g, "");
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function formatMoney(n: number): string {
  return `$${n.toFixed(2).replace(/\.00$/, "")}`;
}

const fmt = formatMoney;

function ensureLabel(raw?: string | null): string | null {
  const n = parsePrice(raw);
  return n === null ? null : fmt(n);
}

/**
 * Compute the discounted (sale) price from a retail total and the attached
 * sale's real type + value. Returns null when the sale doesn't translate to a
 * computable price reduction (no sale, unsupported type, or invalid value), so
 * callers can fall back to the plain retail price / hardcoded literals.
 */
export function computeSalePrice(
  retailTotal: number,
  sale?: SaleLike | null,
): number | null {
  if (!sale || !Number.isFinite(retailTotal) || retailTotal <= 0) return null;
  const type = (sale.saleType ?? "").toLowerCase().trim();
  const value = parsePrice(sale.discountValue);
  if (value === null || value <= 0) return null;
  if (type === "percentage_off") {
    if (value >= 100) return null;
    return round2(retailTotal * (1 - value / 100));
  }
  if (type === "dollar_off") {
    if (value >= retailTotal) return null;
    return round2(retailTotal - value);
  }
  // Other sale types (free_shipping, bogo, bundle, gwp, …) don't map to a
  // simple line-total price reduction — leave pricing at retail.
  return null;
}

/** A single real review surfaced as featured social proof (e.g. the hero). */
export interface LpReviewTop {
  /** Reviewer display name, or null when the source review had none. */
  reviewer: string | null;
  /** Non-empty review body. */
  reviewText: string;
  /** Clamped 1–5 star rating (defaults to 5 when the source had none). */
  rating: number;
}

/** Star rating + review-count strings for the LP, with hardcoded fallbacks. */
export interface LpReviewDisplay {
  /** Average rating, e.g. "4.8" (falls back to "4.8"). */
  ratingText: string;
  /** Total review count, e.g. "320" (falls back to "4,000+"). */
  reviewCountText: string;
  /**
   * True when at least one real review exists across all products. Lets v2
   * social-proof surfaces (rating/count) render only from real data and stay
   * blank otherwise, instead of showing the hardcoded 4.8 / 4,000+ fallback.
   */
  hasReviews: boolean;
  /**
   * The first real review (across all products) with a non-empty body, surfaced
   * read-only as featured social proof. Null when no real review exists, so
   * callers can fall back to editable placeholder copy. Equals `allReviews[0]`.
   */
  topReview: LpReviewTop | null;
  /**
   * Every real review (across all products) with a non-empty body, normalized to
   * LpReviewTop. Drives the hero "Choose review" picker so the user can feature a
   * different review than the default first one. Empty when no reviews exist.
   */
  allReviews: LpReviewTop[];
}

/**
 * Aggregate average rating + total count across every product's reviews, plus
 * the first real review to feature read-only. Falls back to the original
 * hardcoded literals ("4.8" / "4,000+") and a null `topReview` when there are no
 * reviews, so the page never renders blank social proof.
 */
export function computeReviewDisplay(
  reviewsByProduct?: Record<
    string,
    Array<{ rating?: number | null; reviewText?: string | null; reviewer?: string | null }>
  > | null,
): LpReviewDisplay {
  const all = Object.values(reviewsByProduct ?? {}).flat();
  const ratings = all
    .map((r) => (typeof r?.rating === "number" ? r.rating : null))
    .filter((x): x is number => x !== null && x > 0);
  const avg = ratings.length
    ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
    : null;
  const allReviews: LpReviewTop[] = all
    .filter((r) => typeof r?.reviewText === "string" && r.reviewText.trim().length > 0)
    .map((r) => ({
      reviewer: r.reviewer?.trim() || null,
      reviewText: r.reviewText!.trim(),
      rating:
        typeof r.rating === "number" && r.rating > 0
          ? Math.min(5, Math.max(1, Math.round(r.rating)))
          : 5,
    }));
  return {
    ratingText: avg !== null ? avg.toFixed(1) : "4.8",
    reviewCountText: all.length > 0 ? all.length.toLocaleString() : "4,000+",
    hasReviews: all.length > 0,
    topReview: allReviews[0] ?? null,
    allReviews,
  };
}

export function computeReceipt(
  products: Product[] | null | undefined,
  offer?: OfferLike | null,
  sale?: SaleLike | null,
): LpReceipt {
  const list = Array.isArray(products) ? products.filter(Boolean) : [];
  const isMulti = list.length > 1;

  const lineItems: LpReceiptLine[] = list.map((p) => ({
    name: (p as any).name ?? "Product",
    priceLabel: ensureLabel((p as any).priceText),
    desc: (p as any).summary || undefined,
  }));

  const itemPrices = list.map((p) => parsePrice((p as any).priceText));
  const allKnown = itemPrices.length > 0 && itemPrices.every((x) => x !== null);
  const summedRetail = allKnown
    ? (itemPrices as number[]).reduce((a, b) => a + b, 0)
    : null;

  // Retail = the real summed product price(s); the built offer's retailTotal is
  // only a last-resort fallback. The sale price is then derived from the
  // attached sale's actual discount (saleType + discountValue) applied to that
  // retail — never from non-existent sale.retailTotal/priceToday fields.
  const retailTotal = summedRetail ?? parsePrice(offer?.retailTotal);
  const salePrice = retailTotal !== null ? computeSalePrice(retailTotal, sale) : null;
  const priceToday = salePrice ?? retailTotal ?? parsePrice(offer?.priceToday);

  const discountAmount =
    retailTotal !== null && salePrice !== null && retailTotal > salePrice + 0.005
      ? retailTotal - salePrice
      : null;
  const hasDiscount = discountAmount !== null;
  const discountPercent =
    hasDiscount && retailTotal && retailTotal > 0
      ? Math.round((discountAmount! / retailTotal) * 100)
      : null;

  const priceTodayLabel = priceToday !== null ? fmt(priceToday) : "";

  let perDayLabel: string | null = null;
  if (priceToday !== null && priceToday > 0) {
    const perDay = priceToday / 45;
    perDayLabel =
      perDay < 1
        ? `Less than ${Math.ceil(perDay * 100)}¢/day`
        : `About $${perDay.toFixed(2)}/day`;
  }

  const guaranteeDays = offer?.guaranteeDays ?? 45;
  const refillLabel = ensureLabel(offer?.refillPrice);

  const guaranteeBullets = [
    refillLabel ? `${refillLabel} refill ships in ${guaranteeDays} days` : null,
    "Skip, pause or cancel anytime",
    `${guaranteeDays}-Day Money-Back Guarantee`,
    "FREE avg 3-day shipping today",
  ].filter((x): x is string => !!x);

  const ctaText = priceTodayLabel
    ? `GET ${isMulti ? "MY KIT" : "YOURS"} FOR ${priceTodayLabel} TODAY`
    : isMulti
      ? "GET MY KIT TODAY"
      : "GET YOURS TODAY";

  return {
    lineItems,
    isMulti,
    hasDiscount,
    retailLabel: hasDiscount && retailTotal !== null ? fmt(retailTotal) : null,
    discountAmountLabel:
      hasDiscount && discountAmount !== null ? `-${fmt(discountAmount)}` : null,
    discountPercent,
    discountRowLabel: sale?.name?.trim()
      ? sale.name.trim()
      : isMulti
        ? "Bundle savings"
        : "Your discount",
    priceTodayLabel,
    savingsLabel:
      hasDiscount && discountAmount !== null
        ? `You save ${fmt(discountAmount)} today`
        : null,
    perDayLabel,
    refillLabel,
    guaranteeDays,
    guaranteeBullets,
    bundleLabel: isMulti ? "The Farm to Skin Kit" : list[0]?.name ?? "Your Order",
    ctaText,
  };
}
